import { AvatarAccent, InitialsAvatar, Text } from "@growtherapy/sprout-ui";
import type { VideoPlayer } from "@zoom/videosdk";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { RefObject } from "react";
import { useMatch } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ActiveSpeakerOutline, NameBadge } from "../../components";
import { PROVIDER_PATH } from "../../config";
import { nonEssentialElementProps, useIsZoomEnabled } from "../../hooks";
import { UserType } from "../../types";
import { nameToInitials } from "../../utils";
import {
  activelySpeakingParticipantIdsAtom,
  isOwnAudioOffAtom,
  isOwnVideoOffAtom,
  isScreenShareModeAtom,
  parseIdentity,
  useRemoteVideoComponent,
} from "../../video-sdk";
import {
  remoteMediaStatusByParticipantIdAtom,
  userDataFamily,
} from "../../video-sdk/twilio";
import { isWhiteboardModeAtom } from "../messages/state";
import { UseRemoteMediaProps, useRemoteMedia } from "../useRemoteMedia";
import { useTriggerPictureInPicture } from "./useTriggerPictureInPicture";
import { Participant } from "../../video-sdk/core/room/participant/Participant";
import { RemoteParticipantReconnectingOverlayWrapper } from "./reconnect/RemoteParticipantReconnectingOverlay";
import { remoteParticipantConnectionStatesByIdAtom } from "../../video-sdk/core/room/participant/atoms";
import { ParticipantConnectionState } from "../../video-sdk/core/room/participant/types";

export type NameBadgeWrapperProps = {
  className?: string;
  participant: Participant;
};

function NameBadgeWrapper({ className, participant }: NameBadgeWrapperProps) {
  const participantName = participant.displayName;
  const audioStatus = useAtomValue(remoteMediaStatusByParticipantIdAtom)[
    participant.identity
  ];
  const userData = useAtomValue(userDataFamily(participant.identity));

  return (
    <NameBadge
      audioMuted={audioStatus?.isAudioMuted ?? true}
      name={participantName}
      pronouns={userData?.pronouns}
      className={className}
      isSelf={false}
    />
  );
}

export type RemoteParticipantProps = {
  className?: string;
  isProviderView?: boolean;
  isVideoOff?: boolean;
  isOwnAudioOff?: boolean;
  isOwnVideoOff?: boolean;
  isScreenShareMode?: boolean;
  isWhiteboardMode?: boolean;
  isSpeaking?: boolean;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
  audioRef: RefObject<HTMLAudioElement>;
  nameBadgeComponent: typeof NameBadgeWrapper;
  remoteParticipantReconnectingOverlayComponent: typeof RemoteParticipantReconnectingOverlayWrapper;
  isReconnecting?: boolean;
} & UseRemoteMediaProps;

export type RemoteParticipantWrapperProps = Pick<
  RemoteParticipantProps,
  "className"
> &
  UseRemoteMediaProps;

function getVideoOffMessaging(
  subject: string,
  isOwnVideoOff?: boolean,
  isOwnAudioOff?: boolean,
) {
  const tokens = [];
  if (!isOwnVideoOff) tokens.push(" see");
  if (!isOwnAudioOff) tokens.push(" hear");
  const videoOffAction = tokens.join(" and");
  const sentenceEnding =
    isOwnVideoOff && isOwnAudioOff
      ? "."
      : ` but they can still ${videoOffAction} you.`;

  return `${subject} has their camera off${sentenceEnding}`;
}

export function RemoteParticipantCell({
  audioRef,
  className,
  isOwnAudioOff,
  isOwnVideoOff,
  isProviderView,
  isScreenShareMode,
  isWhiteboardMode,
  isSpeaking,
  isVideoOff,
  nameBadgeComponent: NameBadgeComponent,
  remoteParticipant,
  videoRef,
  isReconnecting,
  remoteParticipantReconnectingOverlayComponent:
    RemoteParticipantReconnectingOverlayComponent,
}: RemoteParticipantProps) {
  const RemoteVideo = useRemoteVideoComponent();
  const { id, userType } = parseIdentity(remoteParticipant?.identity);
  const participantName = remoteParticipant?.displayName;

  const remoteParticipantIsProvider = userType === UserType.PROVIDER;
  const displayInitials = participantName
    ? nameToInitials(participantName)
    : "";

  let videoOffMessageSubject = remoteParticipantIsProvider
    ? "Your provider"
    : "This person";

  if (isProviderView) {
    videoOffMessageSubject = "Your client";
  }
  const isPresentationMode = isScreenShareMode || isWhiteboardMode;
  const showVideoOffMessaging = !isPresentationMode;
  const videoOffMessaging = getVideoOffMessaging(
    videoOffMessageSubject,
    isOwnVideoOff,
    isOwnAudioOff,
  );

  const zoomIsEnabled = useIsZoomEnabled();

  return (
    <div
      className={twMerge(
        "relative overflow-hidden bg-neutral-900 rounded-2xl flex justify-center items-center",
        className,
      )}
      style={{
        // Clips reconnect overlay bg blur by rounded border
        clipPath: "inset(0 round 16px)",
      }}
      data-testid={`remote-audio-video:${id}`}
      /* 
         For accessibility: The aria-label and aria-labelledby attributes are not well supported 
         on the HTML5 <audio> and <video> elements at this time. In the meantime, we can 
         add the label to the parent element.
        */
      role="region"
      aria-label="Live remote participant video and audio"
    >
      {isVideoOff && !isReconnecting && (
        <div className="absolute isolate flex flex-col items-center">
          <InitialsAvatar
            initials={displayInitials}
            size={isPresentationMode ? "sm" : "md"}
            className={classNames(
              "text-center flex items-center justify-center",
            )}
            accent={AvatarAccent.Green}
          />
          {showVideoOffMessaging && (
            <Text
              variant={isPresentationMode ? "xs" : "sm"}
              className="text-neutral-600 p-4 text-center rebrand:text-text-inverse"
              data-testid="video-off-messaging"
            >
              {videoOffMessaging}
            </Text>
          )}
        </div>
      )}

      <RemoteVideo
        remoteParticipantId={id}
        audioRef={audioRef}
        videoRef={videoRef}
      />

      <RemoteParticipantReconnectingOverlayComponent
        remoteParticipantIdentity={remoteParticipant.identity}
      />

      {participantName && (
        <NameBadgeComponent
          participant={remoteParticipant}
          className={classNames(
            `absolute bottom-0 left-0 ${nonEssentialElementProps.className}`,
            { "z-[1]": zoomIsEnabled },
          )}
        />
      )}
      <ActiveSpeakerOutline isOutlined={!!isSpeaking} />
    </div>
  );
}

export function RemoteParticipantWrapper({
  remoteParticipant,
  className,
  ...listeners
}: RemoteParticipantWrapperProps) {
  const { audioRef, videoRef } = useRemoteMedia({
    remoteParticipant,
    ...listeners,
  });
  const isOwnAudioOff = useAtomValue(isOwnAudioOffAtom);
  const isOwnVideoOff = useAtomValue(isOwnVideoOffAtom);
  const remoteMediaStatusByParticipantId = useAtomValue(
    remoteMediaStatusByParticipantIdAtom,
  );
  const videoDelegatorStatus =
    remoteMediaStatusByParticipantId[remoteParticipant.identity ?? ""];
  const isVideoOff = videoDelegatorStatus?.isVideoOff ?? true;
  const isScreenShareMode = useAtomValue(isScreenShareModeAtom);
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom);

  const isSpeaking = useAtomValue(activelySpeakingParticipantIdsAtom).has(
    remoteParticipant.identity,
  );
  const isProviderView = !!useMatch(PROVIDER_PATH);
  const connectionState = useAtomValue(
    remoteParticipantConnectionStatesByIdAtom,
  )[remoteParticipant.identity];
  const isReconnecting =
    connectionState === ParticipantConnectionState.Reconnecting;

  useTriggerPictureInPicture(remoteParticipant?.identity ?? "", videoRef);

  return (
    <RemoteParticipantCell
      audioRef={audioRef}
      className={className}
      isOwnAudioOff={isOwnAudioOff}
      isOwnVideoOff={isOwnVideoOff}
      isProviderView={isProviderView}
      isScreenShareMode={isScreenShareMode}
      isWhiteboardMode={isWhiteboardMode.value}
      isSpeaking={isSpeaking}
      isVideoOff={isVideoOff}
      nameBadgeComponent={NameBadgeWrapper}
      remoteParticipant={remoteParticipant}
      videoRef={videoRef}
      isReconnecting={isReconnecting}
      remoteParticipantReconnectingOverlayComponent={
        RemoteParticipantReconnectingOverlayWrapper
      }
    />
  );
}
